import React, { useRef, useEffect, useState, useCallback } from 'react';
import { 
  FaPlay, 
  FaPause, 
  FaVolumeUp, 
  FaExpand, 
  FaCompress,
  FaTachometerAlt,
  FaExclamationTriangle,
  FaSpinner,
  FaPlayCircle,
  FaPauseCircle
} from 'react-icons/fa';
import { 
  FaVolumeXmark
} from 'react-icons/fa6';
import { 
  MdOutlineReplay10,
  MdOutlineForward10
} from 'react-icons/md';
import { VideoJSPlayerProps } from '../types';
import VideoWatermark from './VideoWatermark';
import { useVideoJS } from '../hooks/useVideoJS';
import { useVideoMetrics } from '../hooks/useVideoMetrics';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { useTouchGestures } from '../hooks/useTouchGestures';
import { IS_DEVELOPMENT } from '../../../config';
import { useSnackbar } from '../../../contexts/SnackbarProvider/SnackbarProvider';
import { SnackbarType } from '../../../components/Snackbar/types';
import './VideoJSPlayer.css';

interface DocumentWithFullscreen extends Document {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  mozCancelFullScreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

interface HTMLElementWithFullscreen extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

// iOS の video 要素だけにある標準全画面（video.js の Html5 tech が使う名前に合わせる）
interface HTMLVideoElementWithNativeFullscreen extends HTMLVideoElement {
  webkitEnterFullScreen?: () => void;
}

// play() の Promise は、データが届く前だと再生開始まで決着しない。その間に pause() や load() が
// 走ると Safari は AbortError（The operation was aborted.）で reject する。自動再生の拒否は
// NotAllowedError。どちらもユーザー操作の結果であって障害ではないので、未処理の reject として
// ErrorBoundary（→ /error）まで届かせない。それ以外は原因調査のため console.error に残す
const startPlayback = (play: () => Promise<void> | undefined): void => {
  const playing = play();
  if (!playing) return;
  playing.catch((error: unknown) => {
    // ErrorBoundary の isAbortError と同じ基準（null でないオブジェクトの name）で見る
    const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
    if (name === 'AbortError' || name === 'NotAllowedError') {
      console.warn('再生の要求が取り消された、または拒否された:', error);
      return;
    }
    console.error('再生の開始に失敗:', error);
  });
};

// iPhone で全画面ボタンを受け付ける最小の readyState（HAVE_FUTURE_DATA）。
// iOS の WebKit は、読み込み中の video に webkitEnterFullScreen() を呼ぶと全画面の切り替え状態が
// 詰まったままになり、以後はページ内のどの video でも例外なしで黙って無視される（Safari を
// 完全に終了するまで直らない）。読み込みが進むまでは全画面の呼び出し自体を行わない。
const IOS_FULLSCREEN_MIN_READY_STATE = 3;

const IOS_FULLSCREEN_LOADING_MESSAGE =
  '動画の読み込み中です。再生が始まってから全画面にしてください';

const VideoJSPlayer = (props: VideoJSPlayerProps) => {
  const { className = '', ...otherProps } = props;
  const showSnackbar = useSnackbar();
  
  const containerRef = useRef<HTMLDivElement>(null);
  
  const deviceInfo = useDeviceDetection();
  const { videoRef, playerRef, isReady, error, controls } = useVideoJS(props);
  useVideoMetrics(playerRef.current, props.videoId);
  

  // プレイヤーの状態管理
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false);
  const [showControls, setShowControls] = useState(true);

  // 音量コントロールの状態管理（ミュート機能のみ）

  // プログレスバードラッグの状態管理
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const mouseDownPositionRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const isTouchDraggingRef = useRef<boolean>(false);
  const shouldIgnoreClickRef = useRef<boolean>(false);

  // コントロール自動非表示のタイマー
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const AUTO_HIDE_DELAY = 3000;


  // コントロール表示管理（フルスクリーン時とモバイル版で動作）
  const clearControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
  }, []);

  const resetControlsTimeout = useCallback(() => {
    clearControlsTimeout();
    controlsTimeoutRef.current = setTimeout(() => {
      if (!isDraggingProgress && !showSettings) {
        setShowControls(false);
      }
    }, AUTO_HIDE_DELAY);
  }, [clearControlsTimeout, isDraggingProgress, showSettings]);

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  const showControlsTemporarilyRef = useRef(showControlsTemporarily);
  useEffect(() => {
    showControlsTemporarilyRef.current = showControlsTemporarily;
  }, [showControlsTemporarily]);

  // タッチジェスチャーの設定（ダブルタップでの10秒送り/戻し機能を有効化）
  useTouchGestures(playerRef, deviceInfo, {
    onSeekForward: () => {
      if (controls) {
        const currentTime = controls.getCurrentTime();
        const duration = controls.getDuration();
        if (currentTime !== undefined && duration !== undefined) {
          const newTime = Math.min(currentTime + 10, duration);
          controls.seek(newTime);
        }
      }
    },
    onSeekBackward: () => {
      if (controls) {
        const currentTime = controls.getCurrentTime();
        if (currentTime !== undefined) {
          const newTime = Math.max(currentTime - 10, 0);
          controls.seek(newTime);
        }
      }
    },
    onTogglePlay: () => {
      if (controls) {
        if (controls.isPaused()) {
          startPlayback(controls.play);
        } else {
          controls.pause();
        }
      }
    },
    onShowControls: () => {
      showControlsTemporarily();
    },
    enabled: true
  });

  // マウス動作、キーボード操作、タッチイベントの監視
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleUserActivity = () => {
      showControlsTemporarily();
    };

    const handleMouseLeave = () => {
      clearControlsTimeout();
      setShowControls(false);
    };

    const containerEvents: Array<keyof HTMLElementEventMap> = [
      'mousemove',
      'mousedown',
      'touchstart',
      'touchmove',
      'click'
    ];

    containerEvents.forEach((eventName) => {
      container.addEventListener(eventName, handleUserActivity);
    });
    document.addEventListener('keydown', handleUserActivity);
    container.addEventListener('mouseleave', handleMouseLeave);

    // 初期状態で数秒後にコントロールを非表示
    showControlsTemporarily();

    return () => {
      containerEvents.forEach((eventName) => {
        container.removeEventListener(eventName, handleUserActivity);
      });
      document.removeEventListener('keydown', handleUserActivity);
      container.removeEventListener('mouseleave', handleMouseLeave);
      clearControlsTimeout();
      // プログレスバーのドラッグイベントリスナーをクリーンアップ
      document.removeEventListener('mousemove', handleProgressMouseMove);
      document.removeEventListener('mouseup', handleProgressMouseUp);
      // アニメーションフレームをクリーンアップ
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [showControlsTemporarily, clearControlsTimeout]);



  // SP版でプレイヤー以外の箇所をタップした場合の処理
  useEffect(() => {
    if (!deviceInfo.isMobile) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      // プレイヤーのコンテナ要素内をクリックした場合は何もしない
      if (containerRef.current && containerRef.current.contains(event.target as Node)) {
        return;
      }

      // プレイヤー外をクリックした場合、コントローラーを非表示にする
      setShowControls(false);
      
      // 既存のタイマーをクリア
      clearControlsTimeout();
    };

    // クリックとタッチイベントの両方を監視
    document.addEventListener('click', handleOutsideClick, true);
    document.addEventListener('touchend', handleOutsideClick, true);

    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
      document.removeEventListener('touchend', handleOutsideClick, true);
    };
  }, [deviceInfo.isMobile, clearControlsTimeout]);

  // 再生状態が変わったタイミングで自動的に非表示タイマーをリセット
  useEffect(() => {
    if (isReady) {
      showControlsTemporarily();
    }
  }, [isPlaying, isReady, showControlsTemporarily]);

  // プログレスバーをドラッグしている間はコントロールを表示したままにする
  useEffect(() => {
    if (isDraggingProgress) {
      setShowControls(true);
      clearControlsTimeout();
    } else if (isReady) {
      showControlsTemporarily();
    }
  }, [isDraggingProgress, isReady, clearControlsTimeout, showControlsTemporarily]);

  // 親コンポーネントへの onReady コールバック
  const onReadyCalledRef = useRef(false);
  useEffect(() => {
    if (isReady && !onReadyCalledRef.current) {
      onReadyCalledRef.current = true;
      if (typeof props.onReady === 'function' && playerRef.current) {
        props.onReady(playerRef.current);
      }
    }
  }, [isReady, props.onReady, playerRef]);

  // Fullscreen状態の判定（実際のFullscreen要素に限定）
  const getFullscreenElement = useCallback(() => {
    if (typeof document === 'undefined') return null;
    const doc = document as DocumentWithFullscreen;
    return (
      document.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement ||
      null
    );
  }, []);

  const isContainerFullscreen = useCallback(() => {
    const fullscreenElement = getFullscreenElement();
    const container = containerRef.current;
    if (!fullscreenElement || !container) return false;
    return fullscreenElement === container || container.contains(fullscreenElement);
  }, [getFullscreenElement]);

  // div の Fullscreen API が無く、video 要素の標準全画面（webkitEnterFullScreen）しか使えない端末か。
  // iPhone の Safari がこれにあたる（iPad は div の API がある）。このとき video.js の
  // requestFullscreen は iOS 標準の全画面プレイヤーを開くだけで、こちらの UI は表示されない。
  const usesNativeVideoFullscreen = useCallback(() => {
    const container = containerRef.current as HTMLElementWithFullscreen | null;
    if (!container) return false;
    const hasElementFullscreen = [
      container.requestFullscreen,
      container.webkitRequestFullscreen,
      container.mozRequestFullScreen,
      container.msRequestFullscreen,
    ].some((request) => typeof request === 'function');
    if (hasElementFullscreen) return false;
    const video = container.querySelector('video') as HTMLVideoElementWithNativeFullscreen | null;
    return typeof video?.webkitEnterFullScreen === 'function';
  }, []);

  const updateFullscreenState = useCallback(() => {
    const isActualFullscreen = isContainerFullscreen();
    if (isActualFullscreen) {
      setIsFullscreen(true);
      return;
    }

    // モバイルではVideoJSの擬似fullscreenを無視して誤判定を避ける。
    //
    // iOS 標準の全画面プレイヤーを使う端末（iPhone）も、向き・画面幅にかかわらず無視する。
    // 横向きの iPhone は幅が 768px 以上で isMobile が false になるが、ここで VideoJS の状態を
    // 採ると、標準プレイヤーを閉じたことが VideoJS に伝わらなかったとき true のまま残り、
    // ボタンが「終了」側（何も起きない）を実行し続けて二度と全画面に入れなくなる (#424)。
    // 標準プレイヤーの表示中はこちらの UI が見えないので、常に「入る」側にしておけばよい。
    if (usesNativeVideoFullscreen()) {
      setIsFullscreen(false);
      return;
    }
    const playerFullscreen = playerRef.current?.isFullscreen?.() ?? false;
    setIsFullscreen(!deviceInfo.isMobile && playerFullscreen);
  }, [deviceInfo.isMobile, isContainerFullscreen, usesNativeVideoFullscreen]);

  // コールバックをrefで安定化（依存配列からdurationを除外するため）
  const durationRef = useRef(duration);
  useEffect(() => { durationRef.current = duration; }, [duration]);

  const onDurationRef = useRef(props.onDuration);
  useEffect(() => { onDurationRef.current = props.onDuration; }, [props.onDuration]);

  const onProgressRef = useRef(props.onProgress);
  useEffect(() => { onProgressRef.current = props.onProgress; }, [props.onProgress]);

  // プレイヤーイベントの監視
  useEffect(() => {
    if (!playerRef.current) return;

    const player = playerRef.current;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeChange = () => {
      const muted = player.muted();
      const vol = player.volume();
      if (muted !== undefined) setIsMuted(muted);
      if (vol !== undefined) setVolume(vol);
    };
    const handleTimeUpdate = () => {
      const current = player.currentTime();
      const dur = player.duration();
      if (current !== undefined) setCurrentTime(current);

      // durationが変更された場合のみ更新と通知
      if (dur && dur > 0 && dur !== durationRef.current) {
        setDuration(dur);
        durationRef.current = dur;
        if (onDurationRef.current) {
          onDurationRef.current(dur);
        }
      }

      const progressPercent = dur && current ? (current / dur) * 100 : 0;
      setProgress(progressPercent);

      // 進捗情報を親コンポーネントに通知
      if (onProgressRef.current && dur && current) {
        const buffered = player.buffered();
        const bufferedEnd = buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;

        onProgressRef.current({
          currentTime: current,
          duration: dur,
          played: progressPercent / 100,
          loaded: dur > 0 ? bufferedEnd / dur : 0,
          buffered: dur > 0 ? bufferedEnd / dur : 0
        });
      }
    };
    const handleFullscreenChange = () => {
      updateFullscreenState();
    };
    const handleRateChange = () => {
      const rate = player.playbackRate();
      if (rate !== undefined) setPlaybackRate(rate);
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => {
      setIsLoading(false);
      setHasPlaybackStarted(true);
      props.onPlaybackStarted?.();
    };

    // loadedmetadataイベント専用のハンドラー（早期にdurationを取得）
    const handleLoadedMetadata = () => {
      const dur = player.duration();
      if (dur && dur > 0) {
        setDuration(dur);
        durationRef.current = dur;
        if (onDurationRef.current) {
          onDurationRef.current(dur);
        }
      }
    };

    player.on('play', handlePlay);
    player.on('pause', handlePause);
    player.on('volumechange', handleVolumeChange);
    player.on('timeupdate', handleTimeUpdate);
    player.on('loadedmetadata', handleLoadedMetadata);
    player.on('fullscreenchange', handleFullscreenChange);
    player.on('ratechange', handleRateChange);
    player.on('loadstart', handleLoadStart);
    player.on('canplay', handleCanPlay);
    player.on('waiting', handleWaiting);
    player.on('playing', handlePlaying);

    // 購読より前に発火してしまった分の取りこぼしを埋める。
    //
    // このハンドラ登録は isReady（= player.ready）を待つ1コミット後に走るが、
    // ブラウザにキャッシュ済みの動画では loadstart の直後 30ms ほどで
    // loadedmetadata → canplay → play → playing が一気に発火し切ってしまう。
    // これらは一度きりのイベントなので、取りこぼすと二度と来ない。
    // 初回視聴では再現せず、2回目以降（キャッシュヒット時）にだけ起きる。
    //
    // 取りこぼす対象は playing だけではない点に注意。play は playing より前に
    // 発火するうえ handlePlay は同じ場所で登録しているため、playing を落とすなら
    // play も必ず落ちている。isLoading だけ復元して isPlaying を放置すると、
    // オーバーレイは消えるのに再生/一時停止ボタンが「▶」のまま効かなくなる
    // （既に再生中の要素に play() を呼んでもイベントが出ないため復帰もしない）。
    // 派手な不具合を静かな不具合に変えるだけなので、まとめて同期する。
    if (player.readyState() >= 3) setIsLoading(false);
    if (player.paused() === false) setIsPlaying(true);
    if (player.hasStarted() || player.paused() === false) {
      setIsLoading(false);
      setHasPlaybackStarted(true);
      // 内部状態だけ真にして外部通知を落とすと、LiveStreamDetail のリトライ
      // タイマーが解除されず、正常に再生できている配信を強制リマウントする
      // （LiveStreamDetail.tsx の onPlaybackStarted のコメント参照）。
      // handlePlaying も playing のたびに呼ぶので、多重呼び出しは既存の契約内。
      props.onPlaybackStarted?.();
    }

    return () => {
      // 動画切替時はこのクリーンアップより先に useVideoJS 側が dispose を済ませて
      // playerRef を null にしている。dispose 済みのプレイヤーに off を呼ぶと
      // 内部要素が無く例外になり得るし、そもそも dispose がリスナーを解放済みで
      // 呼ぶ意味がない。まだ現役のプレイヤーのときだけ解除する。
      if (playerRef.current !== player) return;

      player.off('play', handlePlay);
      player.off('pause', handlePause);
      player.off('volumechange', handleVolumeChange);
      player.off('timeupdate', handleTimeUpdate);
      player.off('loadedmetadata', handleLoadedMetadata);
      player.off('fullscreenchange', handleFullscreenChange);
      player.off('ratechange', handleRateChange);
      player.off('loadstart', handleLoadStart);
      player.off('canplay', handleCanPlay);
      player.off('waiting', handleWaiting);
      player.off('playing', handlePlaying);
    };
  }, [isReady, updateFullscreenState]);

  // ブラウザのフルスクリーンイベントを監視
  useEffect(() => {
    const handleDocumentFullscreenChange = () => {
      updateFullscreenState();
    };

    // 各ブラウザ対応のフルスクリーンイベントを登録
    document.addEventListener('fullscreenchange', handleDocumentFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleDocumentFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleDocumentFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleDocumentFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleDocumentFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleDocumentFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleDocumentFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleDocumentFullscreenChange);
    };
  }, [updateFullscreenState]);

  // 設定メニューを外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSettings && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings]);


  // デバイス対応のクラス名
  const containerClass = [
    'videojs-player-container',
    deviceInfo.isMobile ? 'mobile' : 'desktop',
    isFullscreen ? 'videojs-fullscreen' : '',
    className
  ].filter(Boolean).join(' ');

  // 時間のフォーマット
  const formatTime = (seconds: number): string => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 音量に応じたアイコンを取得（ミュート機能のみ）
  const getVolumeIcon = () => {
    if (isMuted) return <FaVolumeXmark />;
    return <FaVolumeUp />;
  };

  // カスタムコントロールのハンドラー
  const handlePlayPause = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pause();
    } else {
      const player = playerRef.current;
      startPlayback(() => player.play());
    }
    showControlsTemporarily();
  };

  const handleMuteToggle = () => {
    if (!playerRef.current) return;
    playerRef.current.muted(!isMuted);
  };

  const handleFullscreenToggle = async () => {
    if (!containerRef.current) return;

    // 読み込み中の iPhone では、どの経路（video.js のフォールバックを含む）でも全画面を呼ばない。
    // 呼ぶと WebKit の全画面状態が詰まり、以後どの動画でも全画面にできなくなるため。
    if (
      !isFullscreen &&
      usesNativeVideoFullscreen() &&
      (playerRef.current?.readyState?.() ?? 0) < IOS_FULLSCREEN_MIN_READY_STATE
    ) {
      showSnackbar(IOS_FULLSCREEN_LOADING_MESSAGE, SnackbarType.INFO);
      showControlsTemporarily();
      return;
    }

    try {
      if (isFullscreen) {
        // フルスクリーン終了
        const doc = document as DocumentWithFullscreen;
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        } else if (playerRef.current?.exitFullscreen) {
          // VideoJSのフルスクリーン終了をフォールバック
          await playerRef.current.exitFullscreen();
        }
      } else {
        // フルスクリーン開始
        const element = containerRef.current as HTMLElementWithFullscreen;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        } else if (playerRef.current?.requestFullscreen) {
          // VideoJSのフルスクリーン開始をフォールバック
          await playerRef.current.requestFullscreen();
        }
      }
    } catch (error) {
      console.warn('ネイティブFullscreen APIでエラー発生。VideoJSフォールバックを試行:', error);
      // エラーが発生した場合はVideoJSのフルスクリーン機能を試す
      try {
        if (playerRef.current) {
          if (isFullscreen) {
            await playerRef.current.exitFullscreen();
          } else {
            await playerRef.current.requestFullscreen();
          }
        }
      } catch (fallbackError) {
        console.error('フルスクリーン切替に失敗しました:', fallbackError);
      }
    }
    showControlsTemporarily();
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    // ドラッグ操作の場合はクリックイベントを無視
    if (!playerRef.current || !duration || shouldIgnoreClickRef.current) {
      shouldIgnoreClickRef.current = false;
      return;
    }
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = duration * percentage;
    
    // クリック時も即座にUIを更新して戻り挙動を防ぐ
    setCurrentTime(newTime);
    setProgress(percentage * 100);
    playerRef.current.currentTime(newTime);
  };

  useEffect(() => {
    if (!progressBarRef.current || !deviceInfo.isMobile) return;

    const progressBar = progressBarRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      try {
        if (!playerRef.current || !duration) return;
        
        // タッチイベントのバリデーション
        if (!e.touches || e.touches.length === 0) return;
        
        // イベントがキャンセル可能な場合のみpreventDefaultを呼ぶ
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        
        const touch = e.touches[0];
        const rect = progressBar.getBoundingClientRect();
        if (!rect) return;
        
        const touchX = touch.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, touchX / rect.width));
        
        isTouchDraggingRef.current = true;
        setIsDraggingProgress(true);
        setPreviewProgress(percentage * 100);
        
        // コントロール表示を固定
        showControlsTemporarilyRef.current();
      } catch (error) {
        if (IS_DEVELOPMENT) {
          console.error('Touch start event error:', error);
        }
        isTouchDraggingRef.current = false;
        setIsDraggingProgress(false);
        setPreviewProgress(0);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      try {
        if (!isTouchDraggingRef.current || !progressBar || !duration) return;
        
        // タッチイベントのバリデーション
        if (!e.touches || e.touches.length === 0) return;
        
        // イベントがキャンセル可能な場合のみpreventDefaultを呼ぶ
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        
        const touch = e.touches[0];
        const rect = progressBar.getBoundingClientRect();
        if (!rect) return;
        
        const touchX = touch.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, touchX / rect.width));
        
        // 既存のアニメーションフレームをキャンセル
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        
        // より滑らかな追従のため、requestAnimationFrameを使用
        animationFrameRef.current = requestAnimationFrame(() => {
          setPreviewProgress(percentage * 100);
        });
      } catch (error) {
        if (IS_DEVELOPMENT) {
          console.error('Touch move event error:', error);
        }
        isTouchDraggingRef.current = false;
        setIsDraggingProgress(false);
        setPreviewProgress(0);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      try {
        if (!isTouchDraggingRef.current || !playerRef.current || !duration || !progressBar) return;
        
        // タッチイベントのバリデーション
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        
        // イベントがキャンセル可能な場合のみpreventDefaultを呼ぶ
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        
        // アニメーションフレームをキャンセル
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        
        // タッチ終了時の位置から直接計算（previewProgressは非同期更新のため信頼できない）
        const touch = e.changedTouches[0];
        const rect = progressBar.getBoundingClientRect();
        if (!rect) return;
        
        const touchX = touch.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, touchX / rect.width));
        const newTime = duration * percentage;
        
        // UIを先に更新して一度戻る挙動を防ぐ
        setCurrentTime(newTime);
        setProgress(percentage * 100);

        // 実際にシーク
        if (playerRef.current) {
          playerRef.current.currentTime(newTime);
        }

        isTouchDraggingRef.current = false;
        setIsDraggingProgress(false);
        
        // プレビュー状態をリセット
        setPreviewProgress(0);
      } catch (error) {
        if (IS_DEVELOPMENT) {
          console.error('Touch end event error:', error);
        }
        isTouchDraggingRef.current = false;
        setIsDraggingProgress(false);
        setPreviewProgress(0);
      }
    };

    // passive: falseを指定してイベントリスナーを追加
    progressBar.addEventListener('touchstart', handleTouchStart, { passive: false });
    progressBar.addEventListener('touchmove', handleTouchMove, { passive: false });
    progressBar.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      if (progressBar) {
        progressBar.removeEventListener('touchstart', handleTouchStart);
        progressBar.removeEventListener('touchmove', handleTouchMove);
        progressBar.removeEventListener('touchend', handleTouchEnd);
      }
      // クリーンアップ時に状態をリセット
      isTouchDraggingRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [deviceInfo.isMobile, duration]);

  // マウスドラッグ対応（デスクトップ版）
  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!playerRef.current || !duration || !progressBarRef.current) return;
    
    // preventDefaultは呼び出さない（クリックイベントを妨げない）
    e.stopPropagation();
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
    
    // マウスダウン位置と時刻を記録
    mouseDownPositionRef.current = { 
      x: e.clientX, 
      y: e.clientY,
      time: Date.now()
    };
    isDraggingRef.current = false;
    shouldIgnoreClickRef.current = false;
    
    // プレビュー位置を設定
    setPreviewProgress(percentage * 100);
    
    document.addEventListener('mousemove', handleProgressMouseMove);
    document.addEventListener('mouseup', handleProgressMouseUp);
  };

  const handleProgressMouseMove = (e: MouseEvent) => {
    if (!progressBarRef.current || !duration || !mouseDownPositionRef.current) return;
    
    // マウスが動いたことを記録（クリックとドラッグを区別するため）
    const moveThreshold = 3; // 3px以上動いたらドラッグとみなす
    const deltaX = Math.abs(e.clientX - mouseDownPositionRef.current.x);
    const deltaY = Math.abs(e.clientY - mouseDownPositionRef.current.y);
    
    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      // ドラッグ操作と判定
      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        setIsDraggingProgress(true);
        shouldIgnoreClickRef.current = true;
      }
      
      e.preventDefault();
      
      const rect = progressBarRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
      
      // 既存のアニメーションフレームをキャンセル
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // より滑らかな追従のため、requestAnimationFrameを使用
      animationFrameRef.current = requestAnimationFrame(() => {
        setPreviewProgress(percentage * 100);
      });
    }
  };

  const handleProgressMouseUp = (e: MouseEvent) => {
    if (!playerRef.current || !duration || !mouseDownPositionRef.current || !progressBarRef.current) return;
    
    // アニメーションフレームをキャンセル
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // ドラッグ操作だった場合のみシークを実行
    if (isDraggingRef.current) {
      e.preventDefault();
      
      // マウスアップ時の位置から直接計算（previewProgressは非同期更新のため信頼できない）
      const rect = progressBarRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
      const newTime = duration * percentage;
      
      // UIを先に更新して一度戻る挙動を防ぐ
      setCurrentTime(newTime);
      setProgress(percentage * 100);
      
      // 実際にシーク
      playerRef.current.currentTime(newTime);
      
      setIsDraggingProgress(false);
      setPreviewProgress(0);
    } else {
      // クリック操作の場合は、clickイベントでシークを実行するため、ここでは何もしない
      // ただし、プレビュー状態はリセット
      setPreviewProgress(0);
    }
    
    // リセット
    isDraggingRef.current = false;
    mouseDownPositionRef.current = null;
    
    document.removeEventListener('mousemove', handleProgressMouseMove);
    document.removeEventListener('mouseup', handleProgressMouseUp);
  };


  const handlePlaybackRateChange = (rate: number) => {
    if (!playerRef.current) return;
    playerRef.current.playbackRate(rate);
    setShowSettings(false);
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  return (
    <div ref={containerRef} className={containerClass}>
      {/* ローディング状態 - 再生開始後はバッファ待ちでオーバーレイを出さない */}
      {(!isReady || (isLoading && !hasPlaybackStarted)) && !error && (
        <div className="videojs-player-loading">
          <FaSpinner className="loading-spinner-icon" />
          <p>動画を読み込み中...</p>
        </div>
      )}
      
      {/* エラー状態 */}
      {error && (
        <div className="videojs-error-display">
          <div className="videojs-error-content">
            <FaExclamationTriangle className="error-icon" />
            <h3>再生エラー</h3>
            <p>{error.message || '動画の再生中にエラーが発生しました'}</p>
          </div>
        </div>
      )}
      
      {/* VideoJSプレイヤー */}
      <div data-vjs-player style={{ width: '100%', height: '100%' }}>
        <video
          ref={videoRef}
          className="video-js vjs-medulla-skin"
          controls={false}
          preload="auto"
          width="100%"
          height="100%"
          data-setup="{}"
          playsInline
        />
      </div>

      {/* 不正拡散抑止・トレーサビリティ用ウォーターマーク (#206) */}
      {isReady && <VideoWatermark />}

      {/* カスタム大きな再生/一時停止ボタン */}
      {isReady && !isLoading && (
        <button 
          className={`custom-big-play-button ${isPlaying ? 'playing' : 'paused'} ${!showControls ? 'hidden' : ''}`}
          onClick={(e) => {
            handlePlayPause();
            e.currentTarget.blur();
            if (isFullscreen) {
              showControlsTemporarily();
            }
          }}
          aria-label={isPlaying ? '動画を一時停止' : '動画を再生'}
        >
          {isPlaying ? (
            <FaPauseCircle className="big-play-icon" />
          ) : (
            <FaPlayCircle className="big-play-icon" />
          )}
        </button>
      )}

      {/* カスタムコントロールバー (React Icons) */}
      {isReady && (
        <>
          {/* コントロールバー */}
          <div className={`custom-control-bar ${!showControls ? 'hidden' : ''}`}>
            {/* プログレスバー */}
            <div 
              className={`custom-progress-bar ${!showControls ? 'hidden' : ''} ${isDraggingProgress ? 'dragging' : ''}`}
              ref={progressBarRef}
              onClick={handleProgressClick}
              onMouseDown={handleProgressMouseDown}
            >
              <div className="custom-progress-track">
                <div 
                  className="custom-progress-fill" 
                  style={{ width: `${isDraggingProgress ? previewProgress : progress}%` }}
                />
                <div 
                  className="custom-progress-handle" 
                  style={{ left: `${isDraggingProgress ? previewProgress : progress}%` }} 
                />
              </div>
            </div>
            {/* 左側のコントロール */}
            <div className="custom-control-group left">
              {/* 再生/一時停止ボタン */}
              <button 
                className="custom-control-button play-button"
                onClick={handlePlayPause}
                aria-label={isPlaying ? '一時停止' : '再生'}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>

              {/* 音量コントロール（ミュート機能のみ） */}
              <div className="custom-volume-control">
                <button 
                  className="custom-control-button volume-button"
                  onClick={handleMuteToggle}
                  aria-label={isMuted ? 'ミュート解除' : 'ミュート'}
                >
                  {getVolumeIcon()}
                </button>
              </div>

              {/* 時間表示 */}
              <div className="custom-time-display">
                <span className="current-time">{formatTime(currentTime)}</span>
                <span className="time-separator"> / </span>
                <span className="duration">{formatTime(duration)}</span>
              </div>
            </div>

            {/* 右側のコントロール */}
            <div className="custom-control-group right">
              {/* 再生速度ボタン */}
              {!deviceInfo.isMobile && (
                <div className="custom-settings-control">
                  <button 
                    className="custom-control-button settings-button"
                    onClick={toggleSettings}
                    aria-label="設定"
                  >
                    <FaTachometerAlt />
                  </button>
                  
                  {/* 再生速度メニュー */}
                  {showSettings && (
                    <div className="custom-settings-menu">
                      <div className="settings-section">
                        <h4>再生速度</h4>
                        {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                          <button
                            key={rate}
                            className={`settings-option ${playbackRate === rate ? 'active' : ''}`}
                            onClick={() => handlePlaybackRateChange(rate)}
                          >
                            {rate === 1 ? '標準' : `${rate}x`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* フルスクリーンボタン */}
              <button 
                className="custom-control-button fullscreen-button"
                onClick={handleFullscreenToggle}
                aria-label={isFullscreen ? 'フルスクリーン終了' : 'フルスクリーン'}
              >
                {isFullscreen ? <FaCompress /> : <FaExpand />}
              </button>
            </div>
          </div>
        </>
      )}



      {/* YouTubeライクな10秒送り・戻しボタン（モバイルのみ画面半分クリック対応） */}
      {deviceInfo.isMobile && isReady && playerRef.current && (
        <div className={`youtube-seek-controls ${!showControls ? 'hidden' : ''}`}>
          <button
            className="youtube-seek-button youtube-seek-backward"
            onClick={(e) => {
              const currentTime = controls.getCurrentTime();
              if (currentTime !== undefined) {
                controls.seek(Math.max(0, currentTime - 10));
              }
              if (isFullscreen) {
                showControlsTemporarily();
              }
              // フォーカスを外してoutlineを非表示にする
              e.currentTarget.blur();
            }}
            title="10秒戻す"
            aria-label="10秒戻す"
          >
            <MdOutlineReplay10 className="seek-icon" />
          </button>
          
          <button
            className="youtube-seek-button youtube-seek-forward"
            onClick={(e) => {
              const currentTime = controls.getCurrentTime();
              if (currentTime !== undefined) {
                controls.seek(currentTime + 10);
              }
              if (isFullscreen) {
                showControlsTemporarily();
              }
              // フォーカスを外してoutlineを非表示にする
              e.currentTarget.blur();
            }}
            title="10秒進む"
            aria-label="10秒進む"
          >
            <MdOutlineForward10 className="seek-icon" />
          </button>
        </div>
      )}

      {/* デスクトップ版：10秒送り・戻しボタン（アイコンクリックのみ） */}
      {!deviceInfo.isMobile && isReady && playerRef.current && (
        <div className={`desktop-seek-controls ${!showControls ? 'hidden' : ''}`}>
          <button
            className="desktop-seek-button desktop-seek-backward"
            onClick={(e) => {
              const currentTime = controls.getCurrentTime();
              if (currentTime !== undefined) {
                controls.seek(Math.max(0, currentTime - 10));
              }
              if (isFullscreen) {
                showControlsTemporarily();
              }
              // フォーカスを外してoutlineを非表示にする
              e.currentTarget.blur();
            }}
            title="10秒戻す (J キー)"
            aria-label="10秒戻す"
          >
            <MdOutlineReplay10 />
          </button>
          
          <button
            className="desktop-seek-button desktop-seek-forward"
            onClick={(e) => {
              const currentTime = controls.getCurrentTime();
              if (currentTime !== undefined) {
                controls.seek(currentTime + 10);
              }
              if (isFullscreen) {
                showControlsTemporarily();
              }
              // フォーカスを外してoutlineを非表示にする
              e.currentTarget.blur();
            }}
            title="10秒進む (L キー)"
            aria-label="10秒進む"
          >
            <MdOutlineForward10 />
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoJSPlayer; 
